import type { AnyEnvelope } from '@wiztime/core';
import type { ChangeEntry, UndoEntry, UndoStep } from './types';

/**
 * Detects changes to a record made outside the undo history (another device, another tab, an
 * expiry purge) so that undo and redo never overwrite them.
 *
 * Undo and redo write records again with new timestamps, so a step cannot simply remember the
 * timestamp it wrote: undoing a later step would invalidate it. Instead the guard keeps, per
 * record, the timestamp last written by the history (`head`) and a counter (`epoch`) that
 * increases whenever an outside change is noticed. A step may be applied only when the stored
 * record still carries the head timestamp and the step belongs to the record's current epoch.
 */
export class HistoryGuard {
  private readonly head = new Map<string, string>();
  private readonly epoch = new Map<string, number>();

  /** A command committed: note its writes and stamp the entries with the record epochs. */
  stamp(changes: readonly ChangeEntry[]): UndoEntry[] {
    return changes.map((change) => {
      const knownHead = this.head.get(change.id);
      if (knownHead !== undefined && change.before?.hlc !== knownHead) this.bump(change.id);
      this.head.set(change.id, change.after.hlc);
      return { ...change, epoch: this.epoch.get(change.id) ?? 0 };
    });
  }

  /** Identifiers of the entries whose record changed outside the history since the step was recorded. */
  staleIds(step: UndoStep, current: ReadonlyArray<AnyEnvelope | undefined>): string[] {
    return step.entries
      .filter(
        (entry, index) =>
          current[index]?.hlc !== this.head.get(entry.id) || entry.epoch !== (this.epoch.get(entry.id) ?? 0),
      )
      .map((entry) => entry.id);
  }

  /** A step was applied: `written` maps each record to the timestamp the step wrote. */
  applied(written: ReadonlyMap<string, string>): void {
    for (const [id, hlc] of written) this.head.set(id, hlc);
  }

  /** An outside change was found on these records: invalidate every step that touches them. */
  resync(current: ReadonlyArray<AnyEnvelope | undefined>, ids: readonly string[]): void {
    ids.forEach((id, index) => {
      this.bump(id);
      const hlc = current[index]?.hlc;
      if (hlc === undefined) this.head.delete(id);
      else this.head.set(id, hlc);
    });
  }

  clear(): void {
    this.head.clear();
    this.epoch.clear();
  }

  private bump(id: string): void {
    this.epoch.set(id, (this.epoch.get(id) ?? 0) + 1);
  }
}
